import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  id?: string;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  required?: boolean;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  dataTestId?: string;
  labelPosition?: 'top' | 'left';
}

/**
 * Custom-styled dropdown (portal-positioned, styled options) — a native <select>
 * can't render this UI, so this implements the WAI-ARIA combobox pattern
 * (role="listbox"/"option" + full keyboard nav below) instead. SonarQube's
 * S6819/S6848 favor native <select>; that trade-off is intentional here.
 */
const Select: React.FC<SelectProps> = ({
  id,
  label,
  error,
  required,
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  disabled = false,
  className = '',
  wrapperClassName,
  dataTestId = 'select',
  labelPosition = 'top'
}) => {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const selectId = id || `select-${generatedId}`;
  const hasError = !!error;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Find selected option label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 250;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Determine if we should open upward
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: openUpward ? buttonRect.top - 4 : buttonRect.bottom + 4,
      left: buttonRect.left,
      width: buttonRect.width,
      openUpward
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update position when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        buttonRef.current?.focus();
        break;
    }
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
    buttonRef.current?.focus();
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        updateDropdownPosition();
      }
      setIsOpen(!isOpen);
      if (!isOpen) {
        setHighlightedIndex(0);
      }
    }
  };

  return (
    <div
      className={clsx(
        labelPosition === 'left' ? styles.selectWrapperHorizontal : styles.selectWrapper,
        wrapperClassName
      )}
    >
      {label && (
        <label
          htmlFor={selectId}
          className={styles.label}
          data-test-id={dataTestId ? `${dataTestId}-label` : undefined}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={labelPosition === 'left' ? styles.selectInputGroup : undefined}>
        <div className={clsx(styles.selectContainer, className)} onKeyDown={handleKeyDown}>
          <button
            ref={buttonRef}
            id={selectId}
            type="button"
            className={clsx(
              styles.selectButton,
              hasError && styles.selectButtonError,
              !selectedOption && styles.selectButtonPlaceholder
            )}
            onClick={toggleDropdown}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-describedby={hasError ? `${selectId}-error` : undefined}
            data-test-id={dataTestId ? `${dataTestId}-select` : undefined}
          >
            <span className={styles.selectButtonText}>{displayText}</span>
            <ChevronDown size={16} className={clsx(styles.selectButtonIcon, isOpen && styles.selectButtonIconOpen)} />
          </button>
        </div>

        {error && (
          <div
            id={`${selectId}-error`}
            className={styles.errorMessage}
            role="alert"
            data-test-id={dataTestId ? `${dataTestId}-error` : 'validation-error'}
          >
            {error}
          </div>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={clsx(styles.selectDropdown, dropdownPosition.openUpward && styles.selectDropdownUpward)}
            data-test-id={`${dataTestId}-dropdown`}
            style={{
              position: 'fixed',
              top: dropdownPosition.openUpward ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`
            }}
          >
            <ul className={styles.selectOptions} role="listbox">
              {options.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  tabIndex={-1}
                  data-test-id={`${dataTestId}-option`}
                  className={clsx(
                    styles.selectOption,
                    option.value === value && styles.selectOptionSelected,
                    index === highlightedIndex && styles.selectOptionHighlighted
                  )}
                  onClick={() => handleSelect(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(option.value);
                    }
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Select;
